import React, { useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { CheckCircle2, Download, Package } from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { API } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const OrderConfirmed = () => {
  const { state } = useLocation();
  const [orderNumber, setOrderNumber] = useState(state?.order_number || "");
  const [email, setEmail] = useState(state?.email || "");
  const [downloads, setDownloads] = useState(state?.downloads || null);
  const [error, setError] = useState("");

  const fetchDownloads = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const r = await api.get(`/orders/${orderNumber}/downloads`, { params: { email } });
      setDownloads(r.data.downloads);
    } catch {
      setError("Commande introuvable ou non payée. Vérifiez le numéro et l'e-mail.");
    }
  };

  return (
    <div className="bg-ivory min-h-[70vh]">
      <Seo title="Commande confirmée" description="Merci pour votre commande." path="/commande-confirmee" noindex />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 text-center">
        <div className="w-20 h-20 mx-auto rounded-full bg-emerald-500/10 flex items-center justify-center mb-6">
          <CheckCircle2 size={44} className="text-emerald-500" />
        </div>
        <h1 className="font-heading text-3xl md:text-4xl font-light text-brown-black" data-testid="confirmation-title">Merci pour votre commande</h1>
        <p className="text-brown-black/70 mt-3">Votre paiement a bien été confirmé.</p>
        {state?.order_number && (
          <p className="mt-2 text-brown-dark font-semibold" data-testid="confirmation-order-number">
            Numéro de commande : {state.order_number}
          </p>
        )}

        {state?.digital_id && (
          <div className="mt-8 bg-white rounded-2xl border border-gold/25 p-6 text-center" data-testid="confirmation-identity">
            <p className="text-xs uppercase tracking-[0.15em] text-brown-dark/50 font-semibold">Votre identité digitale</p>
            <p className="font-heading text-2xl font-semibold text-gold tracking-wide mt-1" data-testid="confirmation-digital-id">{state.digital_id}</p>
            <img src={`${BACKEND_URL}${state.qr_url}`} alt="QR de vérification" className="w-32 h-32 mx-auto mt-3 rounded-lg border border-gold/20" data-testid="confirmation-qr" />
            <a href={`/verification/${state.digital_id}`} className="inline-block mt-3 text-sm text-gold font-semibold hover:underline" data-testid="confirmation-verify-link">
              Vérifier l'authenticité de mon achat
            </a>
            {(state?.email || email) && (
              <div className="mt-4">
                <a
                  href={`${BACKEND_URL}/api/verification/${state.digital_id}/receipt?email=${encodeURIComponent(state?.email || email)}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="confirmation-receipt-link"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brown-dark text-ivory text-sm font-semibold hover:bg-brown-black transition-colors"
                >
                  <Download size={15} /> Télécharger mon reçu / certificat (PDF)
                </a>
              </div>
            )}
          </div>
        )}

        {downloads && downloads.length > 0 ? (
          <div className="mt-10 bg-white rounded-2xl border border-gold/20 p-6 text-left" data-testid="confirmation-downloads">
            <h2 className="font-heading text-xl text-brown-black flex items-center gap-2 mb-4"><Download size={18} className="text-gold" /> Vos téléchargements</h2>
            <ul className="space-y-3">
              {downloads.map((d, i) => (
                <li key={i} className="flex items-center justify-between gap-4 border border-gold/15 rounded-xl px-4 py-3">
                  <div>
                    <p className="font-medium text-brown-black">{d.name}</p>
                    {d.variant_label && <p className="text-xs text-brown-black/50">{d.variant_label}</p>}
                    <p className="text-xs text-brown-black/40">Téléchargements restants : {d.remaining}</p>
                  </div>
                  <a href={`${API}${d.url.replace('/api', '')}`} data-testid={`download-link-${i}`}
                     className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-gold text-white text-sm font-semibold hover:bg-gold-light transition-colors">
                    <Download size={15} /> Télécharger
                  </a>
                </li>
              ))}
            </ul>
          </div>
        ) : downloads && downloads.length === 0 ? (
          <p className="mt-8 text-brown-black/60 flex items-center justify-center gap-2"><Package size={16} /> Aucun fichier numérique associé à cette commande.</p>
        ) : (
          <form onSubmit={fetchDownloads} className="mt-10 bg-white rounded-2xl border border-gold/20 p-6 text-left space-y-4">
            <p className="text-sm text-brown-black/60">Récupérez vos téléchargements avec votre numéro de commande et votre e-mail :</p>
            <input value={orderNumber} onChange={(e) => setOrderNumber(e.target.value)} placeholder="Numéro de commande (PSO-…)" className="w-full border border-gold/30 rounded-lg px-3.5 py-3 focus:border-gold focus:outline-none" data-testid="retrieve-order-number" />
            <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Votre e-mail" className="w-full border border-gold/30 rounded-lg px-3.5 py-3 focus:border-gold focus:outline-none" data-testid="retrieve-email" />
            {error && <p className="text-sm text-red-500">{error}</p>}
            <button type="submit" className="w-full py-3 rounded-full bg-gold text-white font-semibold hover:bg-gold-light transition-colors" data-testid="retrieve-submit">Afficher mes téléchargements</button>
          </form>
        )}

        <Link to="/boutique" className="inline-block mt-10 text-gold font-semibold hover:underline">Retour à la boutique</Link>
      </div>
    </div>
  );
};

export default OrderConfirmed;
